
import { Timestamp } from 'firebase/firestore';
import { LOADING_CALCULATION_BASIS } from '../../constants/systemConfig';

/**
 * Calculates estimated loading time based on defined Loading Rules.
 * Formula: BaseTime + (Amount / Rate).
 * Uses strict basis definition (Weight OR Volume).
 * 
 * @param {number} volume 
 * @param {number} weight 
 * @param {object} loadingRule - The specific loading rule config for the vehicle type
 */
export const calculateLoadTime = (volume, weight, loadingRule = null) => {
    // If no rule is provided, we cannot calculate accurately according to "adaptation".
    if (!loadingRule) return 0;

    const vol = parseFloat(volume) || 0;
    const w = parseFloat(weight) || 0;

    const ruleBase = parseInt(loadingRule.baseLoadTime);
    const baseTime = isNaN(ruleBase) ? 15 : ruleBase;

    // New Logic: Duration per Unit (Minutes per Ton/M3) -> Multiply
    const durationPerUnit = parseFloat(loadingRule.loadingDurationPerUnit);

    // Legacy Logic: Units per Minute (Rate) -> Divide
    const rate = parseFloat(loadingRule.unitsPerMinute);

    let variableTime = 0;

    // Normalize Basis
    const basis = (loadingRule.calculationBasis || '').toLowerCase();
    const targetBasisVolume = LOADING_CALCULATION_BASIS.VOLUME.toLowerCase();
    const targetBasisWeight = LOADING_CALCULATION_BASIS.WEIGHT.toLowerCase();

    if (!isNaN(durationPerUnit) && durationPerUnit > 0) {
        // Multiplication Logic
        if (basis === targetBasisVolume) {
            variableTime = vol * durationPerUnit;
        } else if (basis === targetBasisWeight) {
            variableTime = w * durationPerUnit;
        }
    } else if (!isNaN(rate) && rate > 0) {
        // Division Logic
        if (basis === targetBasisVolume) {
            variableTime = vol / rate;
        } else if (basis === targetBasisWeight) {
            variableTime = w / rate;
        }
    }

    const totalTime = baseTime + variableTime;

    // Return exact time (rounded up to nearest minute)
    return Math.ceil(totalTime);
};

/**
 * Calculates fullness percentage.
 * Returns an object with details.
 */
export const calculateFullnessDetails = (volume, weight, maxVol = 90, maxWeight = 24) => {
    const vol = parseFloat(volume) || 0;
    const w = parseFloat(weight) || 0;
    const mVol = parseFloat(maxVol) || 90;
    const mWeight = parseFloat(maxWeight) || 24;

    const volPct = (vol / mVol) * 100;
    const weightPct = (w / mWeight) * 100;

    // Total fullness is the MAX of either dimension
    const total = Math.max(volPct, weightPct);

    return {
        total: Math.round(total),
        volumePct: Math.round(volPct),
        weightPct: Math.round(weightPct)
    };
};

/**
 * Legacy wrapper for compatibility
 */
export const calculateFullness = (volume, weight, maxVol, maxWeight) => {
    return calculateFullnessDetails(volume, weight, maxVol, maxWeight).total;
};

/**
 * Smart suggestion logic.
 * Returns available slots based on duration and dock schedules.
 * @param {Array} reservations - Existing reservations for the day
 * @param {Array} docks - Available docks
 * @param {Number} durationMinutes - Required time
 * @param {Date} selectedDate - The target date
 */
export const getSuggestions = (reservations, docks, durationMinutes, selectedDate, slotStep = 30, dockTypes = [], vehicleTypeCode = null) => {
    const HOURS_START = 8;
    const HOURS_END = 24; // Extended to 24h for logistics flexibility
    const suggestions = [];

    if (!docks || docks.length === 0) return [];

    // Ensure we have a valid duration to display meaningful slots (default to slotStep if 0)
    const validDuration = durationMinutes > 0 ? durationMinutes : slotStep;

    // Filter docks based on vehicle type compatibility
    let compatibleDocks = docks.filter(dock => dock.status !== 'maintenance');

    if (vehicleTypeCode && dockTypes.length > 0) {
        compatibleDocks = compatibleDocks.filter(dock => {
            // Find the configuration for this dock's type
            const dockTypeCfg = dockTypes.find(dt => dt.code === dock.type);

            // If no config found, default to allowed for safety or maintain legacy behavior
            if (!dockTypeCfg) return true;

            // If allowedVehicleTypes is empty/undefined, it means ALL types are allowed
            if (!dockTypeCfg.allowedVehicleTypes || dockTypeCfg.allowedVehicleTypes.length === 0) return true;

            // Check if the current vehicle type is in the allowed list
            return dockTypeCfg.allowedVehicleTypes.includes(vehicleTypeCode);
        });
    }

    compatibleDocks.forEach(dock => {
        // Find gaps for this dock
        for (let h = HOURS_START; h < HOURS_END; h++) {
            // Use dynamic slot step
            for (let m = 0; m < 60; m += slotStep) {
                const slotStart = new Date(selectedDate);
                slotStart.setHours(h, m, 0, 0);

                const slotEnd = new Date(slotStart.getTime() + validDuration * 60000);

                // Check boundaries
                if (slotEnd.getHours() > HOURS_END && slotEnd.getMinutes() > 0) continue;

                // Check conflicts
                const isOccupied = reservations.some(res => {
                    return (res.dockId === dock.id) &&
                        (slotStart < res.endTime && slotEnd > res.startTime);
                });

                if (!isOccupied) {
                    // Calculate a "score" or "occupancy" mockup
                    // Prefer morning slots (mock preference)
                    const score = 100 - (h * 2);

                    suggestions.push({
                        dockId: dock.id,
                        dockName: dock.name,
                        factoryId: dock.factoryId,
                        doorGroupId: dock.doorGroupId,
                        startTime: slotStart,
                        endTime: slotEnd,
                        score: score,
                        efficiency: Math.floor(Math.random() * 20) + 80 // Mock efficiency 80-100%
                    });
                }
            }
        }
    });

    // innovative sorting: Best score first, limit to 3
    // Filter out past slots if today
    const now = new Date();
    const isToday = selectedDate.getDate() === now.getDate() &&
        selectedDate.getMonth() === now.getMonth() &&
        selectedDate.getFullYear() === now.getFullYear();

    const validSuggestions = suggestions.filter(s => {
        if (isToday) {
            return s.startTime >= now;
        }
        return true;
    });

    return validSuggestions.sort((a, b) => b.score - a.score).slice(0, 3);
};

/**
 * Calculates automatic assignments for waiting reservations.
 * Uses "Earliest Gap - Best Fit" strategy.
 * 
 * @param {Array} waitingReservations - List of reservations waiting to be assigned
 * @param {Array} existingReservations - List of already confirmed reservations
 * @param {Array} docks - List of available docks
 * @param {Object} constraints - Configuration { date, startHour, minDelayMinutes, dockTypes, slotStep }
 * @returns {Array} - List of proposed assignments { reservation, slot: { dockId, startTime, endTime } }
 */
export const calculateAutoAssignments = (waitingReservations, existingReservations, docks, constraints) => {
    const {
        date,
        mode = 'relative',
        startHour = 8,
        minDelayMinutes = 0,
        dockTypes = [],
        slotStep = 30,
        vehicleTypes = [],
        loadingRules = []
    } = constraints;

    const assignments = [];
    const tempReservations = [...existingReservations];

    // Filter compatible docks (skip maintenance)
    const activeDocks = docks.filter(d => d.status !== 'maintenance');

    if (activeDocks.length === 0) return [];

    // 1. IMPROVED GROUPING & SORTING: Process most restricted vehicles first
    const preCalculated = waitingReservations.map(res => {
        // Resolve Type: Try metrics.vehicleTypeCode, then resolve metrics.vehicleType (ID) using vehicleTypes list
        let vCode = res.metrics?.vehicleTypeCode || res.vehicleTypeCode;
        const vId = res.metrics?.vehicleType || res.vehicleType;

        if (!vCode && vId && vehicleTypes.length > 0) {
            const vt = vehicleTypes.find(t => t.id === vId);
            if (vt) vCode = vt.code;
        }

        // Calculate duration (robust fallback)
        let duration = 0;
        if (res.metrics) {
            duration = parseFloat(res.metrics.estimatedDuration || res.estimatedDuration || res.metrics.reservationDuration) || 0;
        } else {
            duration = parseFloat(res.estimatedDuration || res.plannedDuration) || 0;
        }

        // RECALCULATE DURATION if missing or not aligned (Issue Fix)
        if (duration <= 0 && vCode && loadingRules.length > 0) {
            const rule = loadingRules.find(r => r.vehicleTypeCode === vCode);
            if (rule) {
                const vol = res.metrics?.volume || res.plannedVolume || 0;
                const weight = res.metrics?.weight || res.plannedWeight || 0;
                const time = calculateLoadTime(vol, weight, rule);
                duration = Math.ceil(time / slotStep) * slotStep;
            }
        } else if (duration > 0) {
            // Ensure existing duration is also aligned to slotStep
            duration = Math.ceil(duration / slotStep) * slotStep;
        }

        // Final fallback if still 0
        if (duration <= 0) duration = 30;

        // Calculate restrictiveness score for this specific reservation
        let typeCompatibleDocks = activeDocks;
        if (vCode && dockTypes.length > 0) {
            typeCompatibleDocks = activeDocks.filter(dock => {
                const dockTypeCfg = dockTypes.find(dt => dt.code === dock.type);
                if (!dockTypeCfg || !dockTypeCfg.allowedVehicleTypes || dockTypeCfg.allowedVehicleTypes.length === 0) return true;
                return dockTypeCfg.allowedVehicleTypes.includes(vCode);
            });
        }

        return {
            res,
            vCode,
            duration,
            compatibleDocks: typeCompatibleDocks,
            score: typeCompatibleDocks.length // Smaller score = more restricted
        };
    });

    // Sort: most restricted first, then by earliest expected or ID stability
    preCalculated.sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return (a.res.id < b.res.id ? -1 : 1);
    });

    // Strategy Configuration: Determine starting point for search
    let searchStartDate = new Date(date);
    searchStartDate.setHours(0, 0, 0, 0);

    const now = new Date();
    const isToday = searchStartDate.getDate() === now.getDate() &&
        searchStartDate.getMonth() === now.getMonth() &&
        searchStartDate.getFullYear() === now.getFullYear();

    if (mode === 'relative' && isToday) {
        let startTime = new Date(now.getTime() + (minDelayMinutes || 0) * 60000);
        const remainder = startTime.getMinutes() % slotStep;
        if (remainder !== 0) {
            startTime.setMinutes(startTime.getMinutes() + (slotStep - remainder));
        }
        searchStartDate = startTime;
    } else {
        searchStartDate.setHours(startHour, 0, 0, 0);
        // If the selected hour is in the past for today, force it to current time
        if (isToday && searchStartDate < now) {
            let startTime = new Date(now.getTime());
            const remainder = startTime.getMinutes() % slotStep;
            if (remainder !== 0) {
                startTime.setMinutes(startTime.getMinutes() + (slotStep - remainder));
            }
            searchStartDate = startTime;
        }
    }

    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    if (searchStartDate < dayStart) searchStartDate = dayStart;

    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    // 2. PROCESS prioritized reservations
    for (const item of preCalculated) {
        const { res, compatibleDocks, duration } = item;

        if (compatibleDocks.length === 0) continue;

        // FIND EARLIEST SLOT
        let bestSlot = null;
        let currentTime = new Date(searchStartDate);

        // Brute-force simulation
        while (currentTime < dayEnd) {
            let foundInThisTimeStep = false;

            for (const dock of compatibleDocks) {
                const potentialStart = new Date(currentTime);
                const potentialEnd = new Date(potentialStart.getTime() + duration * 60000);

                if (potentialEnd > dayEnd) continue;

                const isOccupied = tempReservations.some(existing => {
                    if (existing.dockId !== dock.id) return false;

                    const getDT = (val) => {
                        if (!val) return null;
                        if (val.toDate) return val.toDate();
                        if (val instanceof Date) return val;
                        return new Date(val);
                    };

                    const eStart = getDT(existing.startTime);
                    const eEnd = getDT(existing.endTime);

                    if (!eStart || !eEnd) return false;
                    return (potentialStart < eEnd && potentialEnd > eStart);
                });

                if (!isOccupied) {
                    bestSlot = {
                        dockId: dock.id,
                        dockName: dock.name,
                        startTime: potentialStart,
                        endTime: potentialEnd,
                        factoryId: dock.factoryId,
                        doorGroupId: dock.doorGroupId
                    };
                    foundInThisTimeStep = true;
                    break;
                }
            }

            if (foundInThisTimeStep) break;
            currentTime.setMinutes(currentTime.getMinutes() + slotStep);
        }

        if (bestSlot) {
            assignments.push({
                reservation: res,
                slot: bestSlot
            });

            tempReservations.push({
                id: `temp_${res.id}`,
                dockId: bestSlot.dockId,
                startTime: bestSlot.startTime,
                endTime: bestSlot.endTime
            });
        }
    }

    return assignments;
};
